import React, { useMemo } from 'react';
import { DatePicker, parseDate } from '@ark-ui/react/date-picker';
import { parseDateTime } from '@internationalized/date';
import { Portal } from '../../../components/Portal';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface DateTimePickerInputProps extends BaseInputProps<string> {
  /** Minimální datum (ISO `YYYY-MM-DD`). */
  min?: string;
  /** Maximální datum (ISO `YYYY-MM-DD`). */
  max?: string;
  /** Krok minut v nabídce (default 5). */
  minuteStep?: number;
  /** BCP-47 locale pro zobrazení data. */
  locale?: string;
  /** Lokalizované popisky. */
  triggerLabel?: string;
  hourLabel?: string;
  minuteLabel?: string;
  timeLabel?: string;
  className?: string;
}

const toDateValue = (iso: string | undefined | null) => {
  if (!iso) return undefined;
  try {
    return parseDate(iso);
  } catch {
    return undefined;
  }
};

const pad = (n: number) => String(n).padStart(2, '0');

const POPOVER_POSITIONING = { placement: 'bottom-start', gutter: 4 } as const;

/**
 * Atomický výběr data a času postavený na Ark UI `DatePicker` (datum) doplněném
 * o výběr hodin/minut. Ark nemá time-picker, proto je čas řešen nativními
 * `<select>` v patičce kalendáře.
 *
 * RHF drží hodnotu jako ISO datetime řetězec `YYYY-MM-DDTHH:mm` (bez sekund).
 * POZOR: tento tvar se NIKDY nepředává do `parseDate` (vyžaduje sekundy a bez
 * nich vyhodí výjimku) — vždy se rozdělí na datum + čas a skládá ručně.
 */
export const DateTimePickerInput: React.FC<DateTimePickerInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  labelId,
  testAttributes,
  min,
  max,
  minuteStep = 5,
  locale = 'cs-CZ',
  triggerLabel = 'Otevřít kalendář',
  hourLabel = 'Hodina',
  minuteLabel = 'Minuta',
  timeLabel = 'Čas',
  className,
}) => {
  const parsed = useMemo(() => {
    if (!value) return { dateISO: '', hour: 0, minute: 0 };
    const [datePart, timePart = '00:00'] = value.split('T');
    const [h, m] = timePart.split(':');
    return { dateISO: datePart, hour: Number(h) || 0, minute: Number(m) || 0 };
  }, [value]);

  // Ark hodnota je `CalendarDateTime` (datum + čas) — díky tomu je čas součástí
  // DateValue, takže se zobrazení v inputu obnoví i při změně času (Ark počítá
  // text z hodnoty) a výběr dne v kalendáři čas zachová (`preserveTime`).
  const dateValue = useMemo(() => {
    if (!parsed.dateISO) return [];
    try {
      // parseDateTime vyžaduje sekundy.
      return [
        parseDateTime(
          `${parsed.dateISO}T${pad(parsed.hour)}:${pad(parsed.minute)}:00`,
        ),
      ];
    } catch {
      return [];
    }
  }, [parsed.dateISO, parsed.hour, parsed.minute]);

  // Krokové minuty + aktuálně uložená minuta (i mimo grid, např. z legacy dat
  // nebo nedělitelného `minuteStep`) — jinak by `<select>` neměl odpovídající
  // `<option>` a spadl by na první (00), čímž by se zobrazená minuta tiše
  // rozešla s uloženou hodnotou.
  const minuteOptions = useMemo(() => {
    const grid = Array.from(
      { length: Math.ceil(60 / minuteStep) },
      (_, i) => i * minuteStep,
    );
    return parsed.minute >= 0 && parsed.minute < 60
      ? Array.from(new Set([...grid, parsed.minute])).sort((a, b) => a - b)
      : grid;
  }, [minuteStep, parsed.minute]);

  const compose = (dateISO: string, h: number, m: number) =>
    dateISO ? `${dateISO}T${pad(h)}:${pad(m)}` : '';

  // Vlastní zobrazení v inputu: lokalizované datum + čas za ním
  // („21. 1. 2026 09:36"). Čas se čte přímo z `CalendarDateTime`, takže je vždy
  // v souladu s vybranou hodnotou.
  const formatDateTime = (date: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
  }) =>
    `${new Intl.DateTimeFormat(locale).format(
      new Date(date.year, date.month - 1, date.day),
    )} ${pad(date.hour ?? 0)}:${pad(date.minute ?? 0)}`;

  const timeDisabled = disabled || readOnly || !parsed.dateISO;

  const ids = useMemo(
    () => ({ input: () => id, label: () => labelId }),
    [id, labelId],
  );

  return (
    <DatePicker.Root
      name={name}
      ids={ids}
      value={dateValue}
      min={toDateValue(min)}
      max={toDateValue(max)}
      locale={locale}
      disabled={disabled}
      readOnly={readOnly}
      format={formatDateTime}
      // Po výběru data zůstává otevřeno, aby šel doplnit čas.
      closeOnSelect={false}
      onValueChange={(details) => {
        const dt = details.value[0];
        if (!dt) {
          onChange('');
          return;
        }
        // Vezmi datovou část (CalendarDate i CalendarDateTime začínají
        // "YYYY-MM-DD") a zkombinuj s aktuálním časem ze selectů — funguje
        // i při prvním výběru data, kdy ještě není co zachovat.
        onChange(compose(dt.toString().slice(0, 10), parsed.hour, parsed.minute));
      }}
      positioning={POPOVER_POSITIONING}
      lazyMount
      unmountOnExit
      className={clsx(styles.colorRoot, className)}
    >
      <DatePicker.Control className={styles.dateControl}>
        {/* Display-only: zobrazená hodnota nese datum i čas (přes `format`).
            Datum se vybírá kalendářem, čas selecty — proto je input readOnly,
            aby `fixOnBlur` nezkoušel parsovat zpět řetězec s časem. */}
        <DatePicker.Input
          ref={ref}
          readOnly
          placeholder={placeholder}
          onBlur={onBlur}
          className={styles.dateInput}
          aria-describedby={describedBy}
          aria-invalid={hasError || undefined}
          {...testAttributes}
        />
        <DatePicker.Trigger
          className={styles.dateTrigger}
          aria-label={triggerLabel}
        >
          <Icon name="calendar" size={18} />
        </DatePicker.Trigger>
      </DatePicker.Control>
      <Portal>
        <DatePicker.Positioner>
          <DatePicker.Content
            className={styles.dateContent}
            data-testid={`${testAttributes['data-testid']}-calendar`}
          >
            <DatePicker.View view="day">
              <DatePicker.Context>
                {(api) => (
                  <>
                    <DatePicker.ViewControl className={styles.dateViewControl}>
                      <DatePicker.PrevTrigger className={styles.datePrevTrigger}>
                        <Icon name="chevron-left" size={16} />
                      </DatePicker.PrevTrigger>
                      <DatePicker.RangeText className={styles.dateViewTrigger} />
                      <DatePicker.NextTrigger className={styles.dateNextTrigger}>
                        <Icon name="chevron-right" size={16} />
                      </DatePicker.NextTrigger>
                    </DatePicker.ViewControl>
                    <DatePicker.Table className={styles.dateTable}>
                      <DatePicker.TableHead>
                        <DatePicker.TableRow>
                          {api.weekDays.map((weekDay, index) => (
                            <DatePicker.TableHeader
                              key={index}
                              className={styles.dateTableHeader}
                            >
                              {weekDay.narrow}
                            </DatePicker.TableHeader>
                          ))}
                        </DatePicker.TableRow>
                      </DatePicker.TableHead>
                      <DatePicker.TableBody>
                        {api.weeks.map((week, weekIndex) => (
                          <DatePicker.TableRow key={weekIndex}>
                            {week.map((day, dayIndex) => (
                              <DatePicker.TableCell key={dayIndex} value={day}>
                                <DatePicker.TableCellTrigger
                                  className={styles.dateCellTrigger}
                                >
                                  {day.day}
                                </DatePicker.TableCellTrigger>
                              </DatePicker.TableCell>
                            ))}
                          </DatePicker.TableRow>
                        ))}
                      </DatePicker.TableBody>
                    </DatePicker.Table>
                    {/* Patička s výběrem času — prosté potomky Content, ne Ark part. */}
                    <div className={styles.dateTimeFooter}>
                      <span className={styles.dateTimeLabel} aria-hidden="true">
                        {timeLabel}
                      </span>
                      <select
                        className={styles.dateTimeSelect}
                        aria-label={hourLabel}
                        disabled={timeDisabled}
                        value={parsed.hour}
                        onChange={(e) =>
                          onChange(
                            compose(
                              parsed.dateISO,
                              Number(e.target.value),
                              parsed.minute,
                            ),
                          )
                        }
                        onBlur={onBlur}
                        data-testid={`${testAttributes['data-testid']}-hour`}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>
                            {pad(h)}
                          </option>
                        ))}
                      </select>
                      <span aria-hidden="true">:</span>
                      <select
                        className={styles.dateTimeSelect}
                        aria-label={minuteLabel}
                        disabled={timeDisabled}
                        value={parsed.minute}
                        onChange={(e) =>
                          onChange(
                            compose(
                              parsed.dateISO,
                              parsed.hour,
                              Number(e.target.value),
                            ),
                          )
                        }
                        onBlur={onBlur}
                        data-testid={`${testAttributes['data-testid']}-minute`}
                      >
                        {minuteOptions.map((m) => (
                          <option key={m} value={m}>
                            {pad(m)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </DatePicker.Context>
            </DatePicker.View>
          </DatePicker.Content>
        </DatePicker.Positioner>
      </Portal>
    </DatePicker.Root>
  );
};

DateTimePickerInput.displayName = 'DateTimePickerInput';
